import {
  EscalationLevelDTO,
  EscalationStepEscalationLevelEnum,
  UrgencyDTO,
  type EscalationStep,
  type HospitalActiveEscalationSteps,
  type HospitalEscalationSettings
} from '@/generated'

import i18n from '@/i18n/i18n'
import type { ConsultationType, TemporaryEscalationStep } from '../types/RoutingSettings'
import {
  EmergencyEscalationLevelDTOToDisplayRole,
  EscalationLevelDTOToDisplayRole
} from '@/util/EscalationLevelDTOToDisplayRole'
import {
  NUMBER_OF_MINUTES_IN_ONE_HOUR,
  NUMBER_OF_SECONDS_IN_ONE_HOUR,
  NUMBER_OF_SECONDS_IN_ONE_MINUTE
} from '@/util/constants'

export const extractSecondsFromEscalationDuration = (duration: string) => {
  const match = duration.match(/\d+/)
  return match ? Number(match[0]) : null
}

export const secondsToTime = (seconds: string) => {
  const secondsNumber = parseInt(seconds)
  const hours = Math.floor(secondsNumber / NUMBER_OF_SECONDS_IN_ONE_HOUR)
  const minutes = Math.floor(
    (secondsNumber % NUMBER_OF_SECONDS_IN_ONE_HOUR) / NUMBER_OF_SECONDS_IN_ONE_MINUTE
  )
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

export const timeToSeconds = (time: string) => {
  const minutes = timeToMinutes(time)
  return minutes * NUMBER_OF_SECONDS_IN_ONE_MINUTE
}

export const timeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * NUMBER_OF_MINUTES_IN_ONE_HOUR + minutes
}

export const escalationStepEscalationLevelEnumToEscalationLevelDTO = (
  escalationLevel: EscalationStepEscalationLevelEnum
): EscalationLevelDTO => {
  return EscalationLevelDTO[escalationLevel]
}

export const allScheduledEscalationLevelsUsedInType = (type: ConsultationType) => {
  return (
    type.escalationSteps.map((step) => step.escalationLevel).length ===
    getScheduledEscalationLevels().length
  )
}

export const allEmergencyEscalationLevelsUsedInType = (type: ConsultationType) => {
  return (
    type.escalationSteps.map((step) => step.escalationLevel).length ===
    getEmergencyEscalationLevels().length
  )
}

export const getScheduledEscalationLevels = () => {
  const scheduledEscalationLevels = [
    EscalationLevelDTO.FIRST_SHIFT,
    EscalationLevelDTO.BACKUP_SHIFT,
    EscalationLevelDTO.ALL_IN_HOSPITAL,
    EscalationLevelDTO.TCC,
    EscalationLevelDTO.SENIOR_PHYSICIAN
  ]

  return scheduledEscalationLevels.map((level) => ({
    title: EscalationLevelDTOToDisplayRole(level),
    value: level
  }))
}

export const getEmergencyEscalationLevels = () => {
  const emergencyEscalationLevels = [
    EscalationLevelDTO.EMERGENCY_TCC,
    EscalationLevelDTO.EMERGENCY_HOSPITAL,
    EscalationLevelDTO.EMERGENCY_SENIOR_PHYSICIAN
  ]

  return emergencyEscalationLevels.map((level) => ({
    title: EmergencyEscalationLevelDTOToDisplayRole(level),
    value: level
  }))
}

export const mapConsultationTypes = (
  initValue: HospitalEscalationSettings | undefined,
  initActiveValue: HospitalActiveEscalationSteps | undefined
): {
  name: string
  enabled: boolean
  timer: string
  urgency: UrgencyDTO
  escalationSteps: TemporaryEscalationStep[]
}[] => {
  return initValue
    ? [
        {
          name: i18n.global.t('Emergency Consultations'),
          enabled: initValue.emergencyEscalationDisabledAt === null,
          timer: secondsToTime(initValue.emergencyEscalationDuration ?? '0'),
          urgency: UrgencyDTO.EMERGENCY,
          escalationSteps:
            initActiveValue?.emergencyConsultationSteps.map(
              (step: EscalationStep, idx: number) => ({
                escalationLevel: escalationStepEscalationLevelEnumToEscalationLevelDTO(
                  step.escalationLevel
                ),
                order: idx + 1
              })
            ) || []
        },
        {
          name: i18n.global.t('Urgent Consultations'),
          enabled: initValue.urgentEscalationDisabledAt === null,
          timer: secondsToTime(initValue.urgentEscalationDuration ?? '0'),
          urgency: UrgencyDTO.URGENT,
          escalationSteps:
            initActiveValue?.urgentConsultationSteps.map((step: EscalationStep, idx: number) => ({
              escalationLevel: escalationStepEscalationLevelEnumToEscalationLevelDTO(
                step.escalationLevel
              ),
              order: idx + 1
            })) || []
        },
        {
          name: i18n.global.t('Routine Consultations'),
          enabled: initValue.routineEscalationDisabledAt === null,
          timer: secondsToTime(initValue.routineEscalationDuration ?? '0'),
          urgency: UrgencyDTO.ROUTINE,
          escalationSteps:
            initActiveValue?.routineConsultationSteps.map((step: EscalationStep, idx: number) => ({
              escalationLevel: escalationStepEscalationLevelEnumToEscalationLevelDTO(
                step.escalationLevel
              ),
              order: idx + 1
            })) || []
        }
      ]
    : []
}
